/*
 * Longeva Aesthetics — collection page enhancer.
 *
 * The file-based collection template is not honoured by the
 * GitHub-bound live theme (it keeps serving the admin-stored
 * section+main layout), so the Selfridges-aligned header is delivered
 * by DOM enhancement instead — the same proven pattern as the PDP
 * enhancer / cart-recs / sitewide drawer.
 *
 * Adds, on collection pages only:
 *   1. A breadcrumb (Home / Collection) above the title for SEO and wayfinding.
 *   2. Hides the merchant collection description, which the reference
 *      suppliers (Ask Pharmacy and similar) do not show on the storefront.
 *      The copy stays in the DOM for crawlers; only the clutter above the
 *      product grid is removed.
 *
 * Companion CSS ships sitewide-inline in longeva-head-additions
 * (propagation-proof). Fully guarded, idempotent, no global overrides.
 */
(function () {
  "use strict";

  // Pathname is reliable at script-execution time; the body
  // `template-collection` class is added by Horizon JS LATER, so gating
  // the whole IIFE on it (with an early return) skipped the observer +
  // retries entirely. Gate per-run on the path instead, and always arm
  // the resilience machinery. Matches /collections/<handle> only (not
  // the /collections list).
  function onCollection() {
    try { return /^\/collections\/[^/]+/.test(location.pathname); }
    catch (e) { return false; }
  }

  function $(s, r) { return (r || document).querySelector(s); }
  function $all(s, r) { return [].slice.call((r || document).querySelectorAll(s)); }

  function mountBreadcrumb() {
    if ($(".longeva-col-breadcrumb")) return;
    var main = $("#MainContent") || $("main") || document.body;
    var h1 = main.querySelector("h1");
    if (!h1) return;
    var titleText = (h1.textContent || "").trim();
    if (!titleText) return;

    // Anchor: the text-block wrapping the H1, else the H1 itself.
    var anchor = h1;
    var tb = h1.closest('[class*="text-block"]');
    if (tb && tb.parentElement) anchor = tb;

    var nav = document.createElement("nav");
    nav.className = "longeva-col-breadcrumb";
    nav.setAttribute("aria-label", "Breadcrumb");

    var home = document.createElement("a");
    home.href = "/";
    home.textContent = "Home";

    var sep = document.createElement("span");
    sep.className = "longeva-col-breadcrumb__sep";
    sep.setAttribute("aria-hidden", "true");
    sep.textContent = "/";

    // Parent category from the menu IA (2026-05-22). Deterministic per
    // collection handle so the crumb is the same on every visit and for
    // crawlers, the SEO-correct way. Collections not in the menu get a
    // plain Home / Collection two-step, no invented parent.
    var TREAT = { name: 'Treatments', url: '/collections/all-products' };
    var CLINIC = { name: 'Clinic Essentials', url: '/collections/consumables' };
    var NAILS = { name: 'Nails', url: '/collections/nails' };
    var SKIN = { name: 'Skincare', url: '/collections/skincare' };
    var PARENT_MAP = {
      'dermal-fillers': TREAT, 'skin-boosters': TREAT, 'polynucleotides': TREAT,
      'fat-dissolvers': TREAT, 'pdo-threads': TREAT, 'body-contouring': TREAT,
      'rhinoplasty': TREAT, 'skin-needling': TREAT, 'microblading-spmu': TREAT,
      'pre-treatment': CLINIC, 'post-treatment': CLINIC, 'sanitisation': CLINIC,
      'gloves-ppe': CLINIC, 'gauze-wipes': CLINIC, 'needles-syringes-1': CLINIC,
      'sharps-disposal': CLINIC,
      'nail-tips': NAILS, 'acrylics-powders': NAILS, 'gel-polish': NAILS,
      'builder-gel': NAILS, 'nail-tools-accessories': NAILS, 'nail-art': NAILS,
      'removers-aftercare': NAILS,
      'cleansers': SKIN, 'exfoliators': SKIN, 'serums': SKIN, 'toners': SKIN,
      'spf': SKIN, 'modeling-face-masks': SKIN, 'post-treatment-skincare': SKIN
    };
    var handle = (location.pathname.split('/collections/')[1] || '').split('/')[0].split('?')[0];
    var parent = PARENT_MAP[handle] || null;

    var cur = document.createElement("span");
    cur.className = "longeva-col-breadcrumb__current";
    cur.setAttribute("aria-current", "page");
    cur.textContent = titleText;

    nav.appendChild(home);
    nav.appendChild(sep);
    if (parent) {
      var pa = document.createElement("a");
      pa.href = parent.url;
      pa.textContent = parent.name;
      var sep2 = document.createElement("span");
      sep2.className = "longeva-col-breadcrumb__sep";
      sep2.setAttribute("aria-hidden", "true");
      sep2.textContent = "/";
      nav.appendChild(pa);
      nav.appendChild(sep2);
    }
    nav.appendChild(cur);

    if (anchor.parentElement) anchor.parentElement.insertBefore(nav, anchor);
  }

  function hideDescription() {
    var main = $("#MainContent") || $("main") || document.body;
    var h1 = main.querySelector("h1");
    var h1Block = h1 ? h1.closest('[class*="text-block"]') : null;

    // The collection description: the richest text-block in the header
    // area that is not the title and sits before the product grid.
    var grid =
      main.querySelector(
        '[class*="product-grid"], .product-grid, .collection .grid, ul[class*="product"]'
      ) || null;
    var blocks = $all('[class*="text-block"], .rte, rte-formatter', main).filter(
      function (el) {
        if (h1Block && (el === h1Block || h1Block.contains(el) || el.contains(h1Block))) return false;
        if (el.querySelector("h1")) return false;
        if (el.classList.contains("longeva-col-desc")) return false;
        var t = (el.textContent || "").trim();
        if (t.length < 120) return false;
        if (grid && (grid.contains(el) || el.contains(grid))) return false;
        // must appear before the grid in document order
        if (grid && (el.compareDocumentPosition(grid) & Node.DOCUMENT_POSITION_FOLLOWING) === 0) return false;
        return true;
      }
    );
    if (!blocks.length) return;
    var src = blocks[0];
    if (src.dataset.lvDesc === "1") return;
    src.dataset.lvDesc = "1";

    // Ask Pharmacy and the other reference suppliers show no collection
    // description on the storefront. Hide it. The copy stays in the DOM
    // (display:none, not removed) so crawlers still read it for SEO; only
    // the clutter above the product grid goes. No foot block, no Read more.
    src.style.display = "none";
  }

  var running = false;
  function run() {
    if (running || !onCollection()) return;
    running = true;
    try { mountBreadcrumb(); } catch (e) {}
    try { hideDescription(); } catch (e) {}
    running = false;
  }

  function start() {
    run();
    // Horizon hydrates / morphs the collection section after first
    // paint, which can land after our injection or wipe it. Re-run on
    // DOM changes (guarded + idempotent: each mount no-ops once its
    // node exists, so this cannot loop) plus staggered retries.
    var main =
      document.querySelector("#MainContent") ||
      document.querySelector("main") ||
      document.body;
    try {
      var t = null;
      var mo = new MutationObserver(function () {
        if (
          document.querySelector(".longeva-col-breadcrumb") &&
          document.querySelector('[data-lv-desc="1"]')
        ) return;
        clearTimeout(t);
        t = setTimeout(run, 120);
      });
      mo.observe(main, { childList: true, subtree: true });
    } catch (e) {}
    [400, 1200, 2500, 4500].forEach(function (ms) { setTimeout(run, ms); });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }
})();
